import type { ComponentType, ReactNode, Ref } from "react";
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Unlicensed drop-in replacement for @virtuoso.dev/message-list.
//
// The upstream component is commercial: on any hostname other than
// localhost/127.0.0.1/0.0.0.0/*.local it renders a red watermark *instead of* the list,
// which blanks the conversation transcript on a deployed origin. This module
// reimplements only the surface the app consumes. It is wired in by a resolve.alias in
// front-spa/vite.config.ts, and only when no licence key is present -- set
// NEXT_PUBLIC_VIRTUOSO_LICENSE_KEY and the real component is used again.
//
// Trade-off: nothing is virtualized, so every message stays mounted. That makes
// height() exact (measured from the DOM) at the cost of long-conversation performance.

export type CustomScrollBehavior = () => {
  animationFrameCount: number;
  easing: (x: number) => number;
};

export type ScrollBehaviorLike = ScrollBehavior | CustomScrollBehavior;

export type ItemLocation =
  | number
  | {
      index: number | "LAST";
      align?: "start" | "center" | "end";
      behavior?: ScrollBehaviorLike;
      purgeItemSizes?: boolean;
      done?: () => void;
    };

export interface ListScrollLocation {
  listOffset: number;
  visibleListHeight: number;
  scrollHeight: number;
  bottomOffset: number;
  isAtBottom: boolean;
  lastVisibleItemIndex: number;
  lastItemBottomOffset: number;
}

type Autoscroll<Data> =
  | boolean
  | ScrollBehaviorLike
  | { location: () => ItemLocation | null | undefined }
  | ((params: {
      scrollLocation: ListScrollLocation;
      scrollInProgress: boolean;
      data: Data[];
    }) => ItemLocation | false | null | undefined);

export interface DataMethods<Data = unknown> {
  get: () => Data[];
  getCurrentlyRendered: () => Data[];
  append: (data: Data[], scrollToBottom?: Autoscroll<Data>) => void;
  prepend: (data: Data[]) => void;
  insert: (
    data: Data[],
    offset: number,
    scrollToBottom?: Autoscroll<Data>
  ) => void;
  map: (
    callbackfn: (data: Data, index: number) => Data,
    autoscrollToBottomBehavior?: Autoscroll<Data>
  ) => void;
  mapWithAnchor: (
    callbackfn: (data: Data, index: number) => Data,
    anchorItemIndex?: number
  ) => void;
  findAndDelete: (predicate: (item: Data, index: number) => boolean) => void;
  findIndex: (
    predicate: (item: Data, index: number, data: Data[]) => boolean
  ) => number;
  find: (
    predicate: (item: Data, index: number, data: Data[]) => boolean
  ) => Data | undefined;
  replace: (
    data: Data[],
    options?: {
      initialLocation?: ItemLocation;
      purgeItemSizes?: boolean;
      suppressItemMeasure?: boolean;
    }
  ) => void;
  deleteRange: (offset: number, count: number) => void;
  batch: (callback: () => void, scrollToBottom?: Autoscroll<Data>) => void;
  removeFromStart: (count: number) => void;
}

export interface VirtuosoMessageListMethods<
  Data = unknown,
  _Context = unknown,
> {
  data: DataMethods<Data>;
  scrollToItem: (location: ItemLocation) => void;
  scrollIntoView: (location: ItemLocation) => void;
  scrollerElement: () => HTMLDivElement | null;
  getScrollLocation: () => ListScrollLocation;
  cancelSmoothScroll: () => void;
  height: (item: Data) => number;
}

type AnyData = Record<string, unknown>;

interface ListProps<Data, Context> {
  data?: { data?: Data[]; scrollModifier?: unknown };
  context: Context;
  ItemContent: ComponentType<{
    index: number;
    data: Data;
    prevData: Data | null;
    nextData: Data | null;
    context: Context;
  }>;
  Header?: ComponentType<{ context: Context }>;
  StickyFooter?: ComponentType<{ context: Context }>;
  EmptyPlaceholder?: ComponentType<{ context: Context }>;
  computeItemKey?: (params: { data: Data; context: Context }) => string;
  itemIdentity?: (item: Data) => string;
  onScroll?: (location: ListScrollLocation) => void;
  onRenderedDataChange?: (data: Data[]) => void;
  className?: string;
  useWindowScroll?: boolean;
  enforceStickyFooterAtBottom?: boolean;
  shortSizeAlign?: string;
  increaseViewportBy?: number;
}

// Sub-pixel scroll positions mean an element pinned to the bottom rarely reports a
// bottomOffset of exactly 0.
const atBottomThresholdPx = 4;

const emptyLocation: ListScrollLocation = {
  listOffset: 0,
  visibleListHeight: 0,
  scrollHeight: 0,
  bottomOffset: 0,
  isAtBottom: true,
  lastVisibleItemIndex: 0,
  lastItemBottomOffset: 0,
};

const MethodsContext = createContext<VirtuosoMessageListMethods | null>(null);
const LocationContext = createContext<ListScrollLocation>(emptyLocation);

export function useVirtuosoMethods<
  Data = unknown,
  Context = unknown,
>(): VirtuosoMessageListMethods<Data, Context> {
  const methods = useContext(MethodsContext);
  if (!methods) {
    throw new Error(
      "useVirtuosoMethods must be used inside VirtuosoMessageList"
    );
  }
  return methods as VirtuosoMessageListMethods<Data, Context>;
}

export function useVirtuosoLocation(): ListScrollLocation {
  return useContext(LocationContext);
}

export function VirtuosoMessageListLicense({
  children,
}: {
  licenseKey?: string;
  children: ReactNode;
}) {
  return <>{children}</>;
}

function resolveIndex(location: ItemLocation, count: number): number {
  if (typeof location === "number") {
    return location;
  }
  const index = (location as { index: number | "LAST" }).index;
  return index === "LAST" ? count - 1 : index;
}

function MessageListInner<Data extends AnyData, Context>(
  props: ListProps<Data, Context>,
  ref: Ref<VirtuosoMessageListMethods<Data, Context>>
) {
  const {
    context,
    ItemContent,
    Header,
    StickyFooter,
    EmptyPlaceholder,
    computeItemKey,
    itemIdentity,
    onScroll,
    onRenderedDataChange,
    className,
    enforceStickyFooterAtBottom,
  } = props;

  const itemsRef = useRef<Data[]>(props.data?.data ?? []);
  const lastPropDataRef = useRef<Data[] | undefined>(props.data?.data);
  const [version, setVersion] = useState(0);
  const batchDepthRef = useRef(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const heightsRef = useRef(new Map<string, number>());
  const itemElementsRef = useRef(new Map<string, HTMLDivElement>());
  const locationRef = useRef<ListScrollLocation>(emptyLocation);
  const [location, setLocation] = useState<ListScrollLocation>(emptyLocation);
  const pendingScrollRef = useRef<ItemLocation | null>(null);
  const didInitialScrollRef = useRef(false);
  const itemsContainerRef = useRef<HTMLDivElement | null>(null);
  const footerContainerRef = useRef<HTMLDivElement | null>(null);
  const stickToBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const lastScrollHeightRef = useRef(0);

  const identityOf = useCallback(
    (item: Data, index: number) => {
      if (itemIdentity) {
        return itemIdentity(item);
      }
      if (computeItemKey) {
        return computeItemKey({ data: item, context });
      }
      return String(index);
    },
    [computeItemKey, context, itemIdentity]
  );

  const notify = useCallback(() => {
    if (batchDepthRef.current === 0) {
      setVersion((v) => v + 1);
    }
  }, []);

  const measureLocation = useCallback((): ListScrollLocation => {
    const el = scrollerRef.current;
    if (!el) {
      return emptyLocation;
    }
    const bottomOffset = el.scrollHeight - el.scrollTop - el.clientHeight;
    return {
      listOffset: -el.scrollTop,
      visibleListHeight: el.clientHeight,
      scrollHeight: el.scrollHeight,
      bottomOffset,
      isAtBottom: bottomOffset <= atBottomThresholdPx,
      lastVisibleItemIndex: Math.max(itemsRef.current.length - 1, 0),
      lastItemBottomOffset: bottomOffset,
    };
  }, []);

  // Callers ask to scroll to the bottom in the same tick as they push new data, so the
  // DOM they want to reveal has not been laid out yet. Rather than scroll to a stale
  // scrollHeight, requests to sit at the end latch stickToBottomRef and the actual
  // scrolling happens here, after every commit and every content resize.
  const pinToBottom = useCallback(() => {
    const el = scrollerRef.current;
    if (!el || !stickToBottomRef.current) {
      return;
    }
    el.scrollTop = el.scrollHeight;
    lastScrollTopRef.current = el.scrollTop;
    lastScrollHeightRef.current = el.scrollHeight;
  }, []);

  const scrollToLocation = useCallback(
    (target: ItemLocation) => {
      const el = scrollerRef.current;
      if (!el) {
        pendingScrollRef.current = target;
        return;
      }
      const index = resolveIndex(target, itemsRef.current.length);
      const align =
        typeof target === "number"
          ? "start"
          : ((target as { align?: string }).align ?? "start");
      const requested =
        typeof target === "number"
          ? "auto"
          : ((target as { behavior?: ScrollBehaviorLike }).behavior ?? "auto");
      // A custom smooth-scroll descriptor is not a DOM ScrollBehavior; the closest
      // faithful mapping is a native smooth scroll.
      const behavior: ScrollBehavior =
        typeof requested === "function" ? "smooth" : requested;

      if (align === "end" || index >= itemsRef.current.length - 1) {
        stickToBottomRef.current = true;
        el.scrollTo({ top: el.scrollHeight, behavior });
        return;
      }
      stickToBottomRef.current = false;
      // Callers scroll to an item they appended in the same tick, before it has
      // rendered: wait for the commit, then measure the real node so the Header and
      // any unmeasured item cannot throw the offset off.
      const item = itemsRef.current[index];
      const node = item && itemElementsRef.current.get(identityOf(item, index));
      if (!node) {
        pendingScrollRef.current = target;
        return;
      }
      el.scrollTo({
        top:
          el.scrollTop +
          node.getBoundingClientRect().top -
          el.getBoundingClientRect().top,
        behavior,
      });
    },
    [identityOf]
  );

  const applyAutoscroll = useCallback(
    (scroll?: Autoscroll<Data>) => {
      if (scroll === false || scroll === undefined || scroll === null) {
        return;
      }
      if (typeof scroll === "function") {
        const result = scroll({
          scrollLocation: measureLocation(),
          scrollInProgress: false,
          data: itemsRef.current,
        });
        if (result) {
          scrollToLocation(result as ItemLocation);
        }
        return;
      }
      if (typeof scroll === "object" && "location" in scroll) {
        const target = scroll.location();
        if (target) {
          scrollToLocation(target);
        }
        return;
      }
      const behavior: ScrollBehaviorLike =
        typeof scroll === "string" || typeof scroll === "function"
          ? scroll
          : "auto";
      scrollToLocation({
        index: "LAST",
        align: "end",
        behavior,
      } as unknown as ItemLocation);
    },
    [measureLocation, scrollToLocation]
  );

  const methods = useMemo(() => {
    const set = (next: Data[]) => {
      itemsRef.current = next;
      notify();
    };
    return {
      data: {
        get: () => itemsRef.current,
        getCurrentlyRendered: () => itemsRef.current,
        append: (data: Data[], scroll?: Autoscroll<Data>) => {
          set([...itemsRef.current, ...data]);
          applyAutoscroll(scroll ?? "auto");
        },
        prepend: (data: Data[]) => set([...data, ...itemsRef.current]),
        insert: (data: Data[], offset: number, scroll?: Autoscroll<Data>) => {
          const next = [...itemsRef.current];
          next.splice(offset, 0, ...data);
          set(next);
          applyAutoscroll(scroll);
        },
        map: (
          callbackfn: (data: Data, index: number) => Data,
          scroll?: Autoscroll<Data>
        ) => {
          set(itemsRef.current.map(callbackfn));
          applyAutoscroll(scroll);
        },
        mapWithAnchor: (callbackfn: (data: Data, index: number) => Data) =>
          set(itemsRef.current.map(callbackfn)),
        findAndDelete: (predicate: (item: Data, index: number) => boolean) =>
          set(
            itemsRef.current.filter((item, index) => !predicate(item, index))
          ),
        findIndex: (
          predicate: (item: Data, index: number, data: Data[]) => boolean
        ) =>
          itemsRef.current.findIndex((item, i) =>
            predicate(item, i, itemsRef.current)
          ),
        find: (
          predicate: (item: Data, index: number, data: Data[]) => boolean
        ) =>
          itemsRef.current.find((item, i) =>
            predicate(item, i, itemsRef.current)
          ),
        replace: (data: Data[]) => set([...data]),
        deleteRange: (offset: number, count: number) => {
          const next = [...itemsRef.current];
          next.splice(offset, count);
          set(next);
        },
        batch: (callback: () => void, scroll?: Autoscroll<Data>) => {
          batchDepthRef.current += 1;
          try {
            callback();
          } finally {
            batchDepthRef.current -= 1;
          }
          setVersion((v) => v + 1);
          applyAutoscroll(scroll);
        },
        removeFromStart: (count: number) => set(itemsRef.current.slice(count)),
      },
      scrollToItem: scrollToLocation,
      scrollIntoView: scrollToLocation,
      scrollerElement: () => scrollerRef.current,
      getScrollLocation: () => locationRef.current,
      cancelSmoothScroll: () => undefined,
      height: (item: Data) => {
        const index = itemsRef.current.indexOf(item);
        return (
          heightsRef.current.get(identityOf(item, index < 0 ? 0 : index)) ?? 0
        );
      },
    } as unknown as VirtuosoMessageListMethods<Data, Context>;
  }, [applyAutoscroll, identityOf, notify, scrollToLocation]);

  useImperativeHandle(ref, () => methods, [methods]);

  const handleScroll = useCallback(() => {
    const el = scrollerRef.current;
    if (el) {
      // Growing content and pinToBottom only ever move scrollTop down the page, so an
      // upward move that is not the browser clamping a shrunken scroller is the user
      // taking over. Returning to the bottom by hand hands control back.
      const movedUp =
        el.scrollTop < lastScrollTopRef.current - atBottomThresholdPx;
      const shrank = el.scrollHeight < lastScrollHeightRef.current;
      if (movedUp && !shrank) {
        stickToBottomRef.current = false;
      } else if (
        el.scrollHeight - el.scrollTop - el.clientHeight <=
        atBottomThresholdPx
      ) {
        stickToBottomRef.current = true;
      }
      lastScrollTopRef.current = el.scrollTop;
      lastScrollHeightRef.current = el.scrollHeight;
    }
    const next = measureLocation();
    locationRef.current = next;
    setLocation(next);
    onScroll?.(next);
  }, [measureLocation, onScroll]);

  const propData = props.data?.data;
  if (propData && propData !== lastPropDataRef.current) {
    lastPropDataRef.current = propData;
    itemsRef.current = propData;
    didInitialScrollRef.current = false;
  }

  const items = itemsRef.current;

  useEffect(() => {
    onRenderedDataChange?.(itemsRef.current);
  }, [version, items, onRenderedDataChange]);

  useEffect(() => {
    if (didInitialScrollRef.current || items.length === 0) {
      return;
    }
    didInitialScrollRef.current = true;
    const target =
      pendingScrollRef.current ??
      ({ index: "LAST", align: "end" } as unknown as ItemLocation);
    pendingScrollRef.current = null;
    scrollToLocation(target);
  }, [items.length, scrollToLocation]);

  useLayoutEffect(() => {
    const pending = pendingScrollRef.current;
    if (pending && didInitialScrollRef.current) {
      pendingScrollRef.current = null;
      scrollToLocation(pending);
      if (pendingScrollRef.current === pending) {
        pendingScrollRef.current = null;
      }
      return;
    }
    pinToBottom();
  }, [version, items, pinToBottom, scrollToLocation]);

  // Streamed markdown keeps growing after it commits -- reasoning blocks expand, code
  // blocks highlight, images decode -- and the composer changes height as it wraps.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => pinToBottom());
    for (const el of [itemsContainerRef.current, footerContainerRef.current]) {
      if (el) {
        observer.observe(el);
      }
    }
    return () => observer.disconnect();
  }, [pinToBottom]);

  const measureItem = useCallback(
    (item: Data, index: number, el: HTMLDivElement | null) => {
      if (!el) {
        itemElementsRef.current.delete(identityOf(item, index));
        return;
      }
      itemElementsRef.current.set(identityOf(item, index), el);
      heightsRef.current.set(identityOf(item, index), el.offsetHeight);
    },
    [identityOf]
  );

  return (
    <MethodsContext.Provider
      value={methods as unknown as VirtuosoMessageListMethods}
    >
      <LocationContext.Provider value={location}>
        <div
          ref={scrollerRef}
          onScroll={handleScroll}
          className={className}
          style={{
            overflowY: "auto",
            overflowAnchor: "none",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Growing to fill the leftover space is what keeps the footer at the
              bottom of a short conversation: `position: sticky` alone only pins the
              footer once the content is tall enough to scroll. */}
          <div
            ref={itemsContainerRef}
            style={{
              flex: enforceStickyFooterAtBottom ? "1 0 auto" : "0 0 auto",
            }}
          >
            {Header && <Header context={context} />}
            {items.length === 0 && EmptyPlaceholder ? (
              <EmptyPlaceholder context={context} />
            ) : (
              items.map((item, index) => (
                <div
                  key={
                    computeItemKey
                      ? computeItemKey({ data: item, context })
                      : identityOf(item, index)
                  }
                  ref={(el) => measureItem(item, index, el)}
                >
                  <ItemContent
                    index={index}
                    data={item}
                    prevData={items[index - 1] ?? null}
                    nextData={items[index + 1] ?? null}
                    context={context}
                  />
                </div>
              ))
            )}
          </div>
          {StickyFooter && (
            <div
              ref={footerContainerRef}
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 1,
                flexShrink: 0,
              }}
            >
              <StickyFooter context={context} />
            </div>
          )}
        </div>
      </LocationContext.Provider>
    </MethodsContext.Provider>
  );
}

export const VirtuosoMessageList = forwardRef(MessageListInner) as <
  Data extends AnyData,
  Context,
>(
  props: ListProps<Data, Context> & {
    ref?: Ref<VirtuosoMessageListMethods<Data, Context>>;
  }
) => React.ReactElement;
